// Module: UgcTextScreen
// Role: S10b keyboard-aware UGC text input screen.
//       Renders a sticky MiniHeader + full-height textarea.
//       When keyboard is open: KeyboardBar (44px, char counter + "Готово") sits
//       flush above keyboard; paddingBottom = kbHeight + 44 + 16.
//       When keyboard is closed: CtaBar shown at bottom.
// Uses: Frame (→IOSScreen), MiniHeader, KeyboardBar, CtaBar, Ic,
//       useKeyboardHeight, flow-types, tokens
// Used by: FlowScreen.tsx (case 'UGC_TEXT')

import { useCallback, useState } from 'react';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { KeyboardBar } from '../atoms/KeyboardBar';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { Ic } from '../../../components/atoms/Ic';
import { useKeyboardHeight } from '../../../hooks/useKeyboardHeight';
import { useSubmitCheckpoint } from '../../../api/flow-client';
import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

// KB_BAR_HEIGHT must match KeyboardBar height (44px).
const KB_BAR_HEIGHT = 44;

// ─── findMinChars — 3-tier lookup ─────────────────────────────────────────────
// Tier 1: own checkpoint validators[0].params.min_chars
// Tier 2: parent checkpoint (same checkpoints list, parent_checkpoint_id) validators[0].params.min_chars
// Tier 3: fallback 80
export function findMinChars(
  checkpoint: Checkpoint,
  checkpoints: Checkpoint[],
): number {
  // Tier 1: own validators
  const ownVal = checkpoint.validators?.[0]?.params?.min_chars;
  if (typeof ownVal === 'number' && ownVal > 0) return ownVal;

  // Tier 2: parent checkpoint validators
  if (checkpoint.parent_checkpoint_id) {
    const parent = checkpoints.find((c) => c.id === checkpoint.parent_checkpoint_id);
    const parentVal = parent?.validators?.[0]?.params?.min_chars;
    if (typeof parentVal === 'number' && parentVal > 0) return parentVal;
  }

  // Tier 3: fallback
  return 80;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface UgcTextScreenProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function UgcTextScreen({
  checkpoint,
  flowRun,
  checkpoints,
}: UgcTextScreenProps) {
  const [text, setText] = useState('');
  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const kbHeight = useKeyboardHeight();
  const keyboardOpen = kbHeight > 0;
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  const minChars = findMinChars(checkpoint, checkpoints);
  const trimmedText = text.trim();
  const canSubmit = trimmedText.length >= minChars;
  const isSubmitting = submitCheckpoint.isPending;

  const screenTitle = checkpoint.display_name ?? 'Текст отзыва';
  const helperText = checkpoint.instructions_text ?? 'Прямо сюда. Потом опубликуешь на маркетплейсе';

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || isSubmitting) return;
    setHintMessage(null);
    try {
      await submitCheckpoint.mutateAsync({
        artefacts: [{ kind: 'text', value: trimmedText }],
      });
    } catch {
      setHintMessage('Ошибка отправки текста. Попробуйте ещё раз.');
    }
  }, [canSubmit, isSubmitting, submitCheckpoint, trimmedText]);

  // Textarea bottom padding: clear KeyboardBar + gap when keyboard is open,
  // or clear CtaBar (approx 80px) when keyboard is closed.
  const textareaPaddingBottom = keyboardOpen
    ? kbHeight + KB_BAR_HEIGHT + 16
    : 96; // CtaBar clearance

  const isEmpty = text.length === 0;

  return (
    <Frame>
      <div data-testid="ugc-text-header">
        <MiniHeader title={screenTitle} />
      </div>

      {/* Content stays in normal document flow; absolute layout puts CTA at top in IOSScreen. */}
      <div
        style={{
          paddingBottom: keyboardOpen ? textareaPaddingBottom : 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Empty-state helper stays visible on focus; otherwise keyboard-open looked like UI disappeared. */}
        {isEmpty && (
          <div style={{ padding: keyboardOpen ? '8px 16px 0' : '12px 16px 0' }}>
            <div style={{ font: T.title3, color: S.label, lineHeight: 1.2, marginBottom: 4 }}>
              Напиши текст отзыва
            </div>
            <div style={{ font: T.footnote, color: S.secondaryLabel, marginBottom: keyboardOpen ? 8 : 14 }}>
              {helperText}
            </div>
          </div>
        )}

        {/* Textarea */}
        <div style={{
          padding: isEmpty ? '0 16px' : '12px 16px 0',
        }}>
          <textarea
            data-testid="ugc-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={isEmpty ? 'Расскажи своими словами, что понравилось...' : 'Поделитесь впечатлениями о товаре...'}
            style={{
              width: '100%',
              minHeight: keyboardOpen ? 148 : isEmpty ? 160 : 220,
              padding: isEmpty ? '16px 14px' : '12px 16px',
              boxSizing: 'border-box',
              background: P.card,
              border: isEmpty
                ? `1.5px dashed rgba(192,81,58,0.45)`
                : `1px solid ${P.line}`,
              outline: 'none',
              resize: 'none',
              fontFamily: T.fontBody,
              fontSize: 16,
              lineHeight: 1.5,
              color: P.ink,
              WebkitAppearance: 'none',
              caretColor: P.terracotta,
              borderRadius: 16,
            }}
          />
          {isEmpty && (
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '8px 2px 0',
            }}>
              <div style={{
                fontFamily: T.fontMono, fontSize: 12, color: P.muted, fontWeight: 600,
              }}>
                0 / {minChars}
              </div>
              <div style={{
                font: T.caption, color: P.terraDark, fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Ic name="arrowUR" size={11} color={P.terraDark} />
                Нажми, чтобы писать
              </div>
            </div>
          )}
        </div>

        {/* Character counter — shown when typing */}
        {!keyboardOpen && text.length > 0 && (
          <div style={{ padding: '4px 16px', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{
              fontFamily: T.fontMono, fontSize: 12,
              color: canSubmit ? P.success : text.length > 0 ? P.warn : P.muted,
              fontWeight: 600,
            }}>
              {text.length}
            </span>
            <span style={{ fontFamily: T.fontMono, fontSize: 12, color: P.muted }}>
              / {minChars}
            </span>
            {canSubmit && (
              <span style={{ font: T.caption, color: P.success, fontWeight: 700, marginLeft: 4 }}>
                хватит
              </span>
            )}
          </div>
        )}

        {/* Guidance card — what's important */}
        <div style={{ padding: '12px 16px 0' }}>
          <div style={{
            background: P.card, borderRadius: 14, padding: 12,
            border: `1px solid ${P.line}`,
          }}>
            <div style={{ font: T.caption, fontWeight: 700, color: S.label, marginBottom: 8 }}>
              Что важно:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { ok: true,  text: `Минимум ${minChars} символов` },
                { ok: true,  text: 'Своими словами — что понравилось' },
                { ok: false, text: 'Без ссылок и контактов' },
                { ok: false, text: 'Не только эмодзи' },
              ].map(({ ok, text: itemText }, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ height: 19, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <Ic name={ok ? 'check' : 'cross'} size={ok ? 14 : 12} color={ok ? P.success : P.error} />
                  </div>
                  <div style={{ flex: 1, font: T.footnote, color: S.label, lineHeight: '19px' }}>
                    {itemText}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KeyboardBar — shown only when keyboard is open */}
      {keyboardOpen && (
        <KeyboardBar
          charCount={text.length}
          minChars={minChars}
          bottom={kbHeight}
          onSubmit={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        />
      )}

      {hintMessage && (
        <div style={{
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: keyboardOpen ? kbHeight + KB_BAR_HEIGHT + 8 : 88,
          zIndex: 20,
          padding: '10px 14px',
          borderRadius: 14,
          background: 'rgba(176, 58, 46, 0.10)',
          color: P.error,
          font: T.footnote,
          textAlign: 'center',
        }}>
          {hintMessage}
        </div>
      )}

      {/* WizardCtaBar — shown only when keyboard is closed */}
      {!keyboardOpen && (
        <WizardCtaBar
          label={isSubmitting ? 'Отправка...' : 'Продолжить'}
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
        />
      )}
    </Frame>
  );
}
